import { computed, ref } from 'vue'
import type { LocationQueryRaw, RouteLocationNormalizedLoaded } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../../services/api'

/**
 * URL-driven state, fetch, and pagination for the enriched accounts list.
 * Follows the same pattern as useActionsQuery / useCandidateQuery.
 */

export const ACCOUNTS_PAGE_SIZE = 50

export type AccountsFilters = {
  q: string
  play: string
  sort: string
  engagement: string
  completed_actions: string
  pending_actions: string
  page: number
}

export const DEFAULT_FILTERS: AccountsFilters = {
  q: '',
  play: '',
  sort: 'name_asc',
  engagement: '',
  completed_actions: '',
  pending_actions: '',
  page: 1,
}

export const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A → Z' },
  { value: 'name_desc', label: 'Name Z → A' },
  { value: 'engagement_newest', label: 'Newest engagement' },
  { value: 'engagement_oldest', label: 'Oldest engagement' },
]

const VALID_SORTS = new Set(SORT_OPTIONS.map((o) => o.value))

const VALID_TRISTATE = new Set(['', 'yes', 'no'])

export function parseFilters(route: RouteLocationNormalizedLoaded): AccountsFilters {
  const str = (key: string, fallback = '') => {
    const raw = route.query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' && value.trim() ? value.trim() : fallback
  }
  const tristate = (key: string) => {
    const v = str(key)
    return VALID_TRISTATE.has(v) ? v : ''
  }
  const page = Number.parseInt(str('p', '1'), 10)
  const sort = str('sort', DEFAULT_FILTERS.sort)
  return {
    q: str('q'),
    play: str('play'),
    sort: VALID_SORTS.has(sort) ? sort : DEFAULT_FILTERS.sort,
    engagement: tristate('engagement'),
    completed_actions: tristate('completed_actions'),
    pending_actions: tristate('pending_actions'),
    page: Number.isFinite(page) && page > 0 ? page : 1,
  }
}

export function filtersToQuery(filters: AccountsFilters): LocationQueryRaw {
  const query: LocationQueryRaw = {}
  if (filters.q) query.q = filters.q
  if (filters.play) query.play = filters.play
  if (filters.sort && filters.sort !== DEFAULT_FILTERS.sort) query.sort = filters.sort
  if (filters.engagement) query.engagement = filters.engagement
  if (filters.completed_actions) query.completed_actions = filters.completed_actions
  if (filters.pending_actions) query.pending_actions = filters.pending_actions
  if (filters.page > 1) query.p = String(filters.page)
  return query
}

// ── Types matching the API response ──
export type AccountPlayMembership = {
  play_id: string
  play_name: string
  state: string
  last_run_id: string | null
  last_run_status: string | null
  last_run_at: string | null
}

export type AccountReachoutPill = {
  id: string
  type: string
  first_name: string
  sent: boolean
  status_badge: 'bounced' | 'replied' | 'deleted' | null
}

export type AccountActivityPill = {
  type: string
  id: string
  account_id: string
  first_name: string
  occurred_at: string | null
  direction: 'in' | 'out' | null
  label: string
  data: Record<string, any> | null
}

export type ActivityTimeline = {
  engaged: number[]
  outbound: number[]
}

export type EnrichedAccountItem = {
  id: string
  domain: string
  name: string | null
  contact_count: number
  plays: AccountPlayMembership[]
  outbound_pills: AccountReachoutPill[]
  outbound_count: number
  engaged_pills: AccountActivityPill[]
  engaged_count: number
  activity_timeline: ActivityTimeline
  created_at: string | null
}

// ── Module-scope cache ──
const cachedItems = ref<EnrichedAccountItem[]>([])
const cachedTotal = ref(0)
let cacheKey = ''
let cacheAt = 0
const CACHE_MAX_AGE_MS = 2 * 60 * 1000

export function useAccountsQuery() {
  const route = useRoute()
  const router = useRouter()

  const items = cachedItems
  const total = cachedTotal
  const loading = ref(false)
  const error = ref('')

  const filters = computed(() => parseFilters(route))
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / ACCOUNTS_PAGE_SIZE)))
  const rangeStart = computed(() => (total.value === 0 ? 0 : (filters.value.page - 1) * ACCOUNTS_PAGE_SIZE + 1))
  const rangeEnd = computed(() => Math.min(filters.value.page * ACCOUNTS_PAGE_SIZE, total.value))
  const rangeLabel = computed(() => {
    if (total.value === 0) return 'No accounts'
    return `${rangeStart.value}\u2013${rangeEnd.value} of ${total.value}`
  })

  function cacheKeyFor(): string {
    return JSON.stringify(filters.value)
  }

  function cacheIsUsable(): boolean {
    return cacheKey === cacheKeyFor()
      && cachedItems.value.length > 0
      && Date.now() - cacheAt < CACHE_MAX_AGE_MS
  }

  function mergeItems(incoming: EnrichedAccountItem[]) {
    const existing = new Map(items.value.map((item) => [item.id, item]))
    items.value = incoming.map((next) => {
      const prev = existing.get(next.id)
      if (!prev) return next
      const changed = prev.engaged_count !== next.engaged_count
        || prev.outbound_count !== next.outbound_count
        || prev.contact_count !== next.contact_count
        || prev.plays.length !== next.plays.length
        || prev.outbound_pills.length !== next.outbound_pills.length
      return changed ? next : prev
    })
  }

  async function load({ forPoll = false } = {}) {
    if (!forPoll) loading.value = true
    error.value = ''
    const f = filters.value
    const params = new URLSearchParams()
    if (f.q) params.set('q', f.q)
    if (f.play) params.set('sales_play_id', f.play)
    params.set('sort', f.sort)
    if (f.engagement) params.set('engagement', f.engagement)
    if (f.completed_actions) params.set('completed_actions', f.completed_actions)
    if (f.pending_actions) params.set('pending_actions', f.pending_actions)
    params.set('limit', String(ACCOUNTS_PAGE_SIZE))
    params.set('offset', String((f.page - 1) * ACCOUNTS_PAGE_SIZE))
    try {
      const data = await apiFetch(`/accounts/enriched?${params.toString()}`)
      const incoming: EnrichedAccountItem[] = Array.isArray(data?.items) ? data.items : []
      if (cacheKey === cacheKeyFor()) mergeItems(incoming)
      else items.value = incoming
      total.value = Number(data?.total) || 0
      cacheKey = cacheKeyFor()
      cacheAt = Date.now()
    } catch (e: any) {
      error.value = e?.message || 'Failed to load accounts.'
    } finally {
      loading.value = false
    }
  }

  function setFilters(patch: Partial<AccountsFilters>) {
    const next = { ...filters.value, ...patch }
    if (!('page' in patch)) next.page = 1
    void router.push({
      name: 'accounts-list',
      query: filtersToQuery(next),
    })
  }

  function goToPage(page: number) {
    const clamped = Math.min(Math.max(1, page), pageCount.value)
    if (clamped === filters.value.page) return
    setFilters({ page: clamped })
  }

  function openDetail(id: string) {
    void router.push({
      name: 'account-detail',
      params: { accountId: id },
    })
  }

  /** Return the id after a given id in the current page, crossing page boundary if needed. */
  async function idAfter(currentId: string): Promise<string | null> {
    const idx = items.value.findIndex((i) => i.id === currentId)
    if (idx >= 0 && idx < items.value.length - 1) return items.value[idx + 1].id
    if (filters.value.page < pageCount.value) {
      goToPage(filters.value.page + 1)
      return null // will re-render with new page
    }
    return null
  }

  async function idBefore(currentId: string): Promise<string | null> {
    const idx = items.value.findIndex((i) => i.id === currentId)
    if (idx > 0) return items.value[idx - 1].id
    if (filters.value.page > 1) {
      goToPage(filters.value.page - 1)
      return null
    }
    return null
  }

  return {
    items,
    total,
    loading,
    error,
    filters,
    pageCount,
    rangeStart,
    rangeEnd,
    rangeLabel,
    cacheIsUsable,
    load,
    setFilters,
    goToPage,
    openDetail,
    idAfter,
    idBefore,
  }
}
